"use client";

import { useMemo, useState } from "react";
import dynamic from "next/dynamic";
import {
  ArrowRight,
  CalendarDays,
  LockKeyhole,
  MessageCircle,
  Send,
  Users2,
} from "lucide-react";
import { cn, formatPrice, lowestTierPrice } from "@/lib/format";
import { normalizeTier } from "@/lib/trip/normalize";
import { formatRange, statusLabel } from "@/components/trip/DeparturesPanel";
import {
  MonthYearFilter,
  type MonthOption,
} from "@/components/trip/MonthYearFilter";
import { Badge } from "@/components/ui/Badge";
import { BookingWizard } from "@/components/trip/BookingWizard";
import { InquiryDialog } from "@/components/trip/InquiryDialog";
import type {
  PackageTier,
  Departure,
  GroupPrice,
  PackageAddon,
  Media,
} from "@/lib/dto";

const DatePicker = dynamic(
  () =>
    import("@/components/home/LazyDayPicker").then(
      (module) => module.DayPicker,
    ),
  {
    ssr: false,
    loading: () => (
      <div className="grid min-h-[200px] place-items-center text-sm text-muted-soft">
        Loading calendar…
      </div>
    ),
  },
);

type Props = {
  tier: PackageTier;
  /** Tier slug from the tier map — identifies which tier is being booked. */
  tierKey?: string;
  currency: string;
  departures?: Departure[];
  packageTitle: string;
  packageSlug: string;
  packageId?: number;
  pricing?: {
    group_prices: GroupPrice[];
    tiers: Record<string, PackageTier> | null;
  };
  addons?: PackageAddon[];
  featuredImage?: Media | null;
  duration?: number;
  durationUnit?: string;
};

function safeDate(value: string): Date | null {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

const DEPARTURE_MONTH = new Intl.DateTimeFormat("en-US", { month: "short" });
const DEPARTURE_DAY = new Intl.DateTimeFormat("en-US", { day: "2-digit" });

export function TierBookingSidebar({
  tier,
  tierKey = "",
  currency,
  departures = [],
  packageTitle,
  packageSlug,
  packageId,
  pricing,
  addons,
  featuredImage,
  duration,
  durationUnit,
}: Props) {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);

  const months = useMemo<MonthOption[]>(() => {
    const grouped = new Map<string, MonthOption>();
    for (const departure of departures) {
      const date = safeDate(departure.startDate);
      if (!date) continue;
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      const current = grouped.get(key);
      if (current) {
        current.count += 1;
      } else {
        grouped.set(key, {
          key,
          year: date.getFullYear(),
          month: date.getMonth(),
          label: key,
          count: 1,
        });
      }
    }
    return [...grouped.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [departures]);

  const [activeMonthKey, setActiveMonthKey] = useState(months[0]?.key ?? "");
  const activeMonth =
    months.find((month) => month.key === activeMonthKey) ?? months[0];
  const years = useMemo(
    () => [...new Set(months.map((month) => month.year))].sort((a, b) => a - b),
    [months],
  );
  const visibleDepartures = useMemo(() => {
    if (!activeMonth) return [];
    return departures.filter((departure) => {
      const date = safeDate(departure.startDate);
      return (
        date?.getFullYear() === activeMonth.year &&
        date.getMonth() === activeMonth.month
      );
    });
  }, [activeMonth, departures]);
  const tierRowPrice = useMemo(() => lowestTierPrice(tier), [tier]);
  const hasDepartures = departures.length > 0;

  // Resolve the tier's own pricing (default price + its group-price brackets)
  // once, and hand it to the wizard. Without this the wizard falls back to the
  // package-level `group_prices`, which is empty for tier-priced packages —
  // every total then computes to zero.
  const normalizedTier = useMemo(
    () => normalizeTier(tierKey, tier),
    [tierKey, tier],
  );

  const wizardProps = {
    packageTitle,
    packageSlug,
    packageId,
    tier: normalizedTier,
    pricing,
    addons,
    featuredImage,
    duration,
    durationUnit,
  };

  return (
    <>
      <div className="overflow-hidden rounded-md border border-line bg-white mt-6">
        <div className="flex items-center justify-between gap-3 border-b border-line bg-paper/25 px-4 pt-4 pb-3">
          <div className="min-w-0 shrink-0">
            <h4 className="font-display text-md font-extrabold leading-tight text-headings">
              Departure Dates
            </h4>
            {hasDepartures ? (
              <p className="text-[11px] text-muted" aria-live="polite">
                {departures.length} scheduled date
                {departures.length === 1 ? "" : "s"} available
              </p>
            ) : null}
          </div>
          {hasDepartures ? (
            <div
              className="flex max-w-[58%] gap-1 overflow-x-auto py-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              role="tablist"
              aria-label="Departure year"
            >
              {years.map((year) => {
                const selected = year === activeMonth?.year;
                return (
                  <button
                    key={year}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    onClick={() => {
                      const firstMonth = months.find(
                        (month) => month.year === year,
                      );
                      if (firstMonth) setActiveMonthKey(firstMonth.key);
                    }}
                    className={cn(
                      "min-h-6 shrink-0 rounded-sm border px-3 text-[10px] font-extrabold tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-headings/20",
                      selected
                        ? "border-headings bg-headings text-white"
                        : "border-line bg-white text-muted hover:border-headings/35 hover:text-headings",
                    )}
                  >
                    {year}
                  </button>
                );
              })}
            </div>
          ) : (
            <CalendarDays className="h-4 w-4 text-primary" aria-hidden />
          )}
        </div>

        <div className="px-3.5 pb-3.5 sm:px-4 sm:pb-4">
          {hasDepartures ? (
            <>
              <MonthYearFilter
                months={months}
                activeKey={activeMonthKey}
                onChange={setActiveMonthKey}
                className="mb-3 border-b border-b-border -mx-4 px-4"
              />

              <div className="departure-scroll max-h-[520px] divide-y divide-line overflow-y-auto overscroll-contain lg:max-h-[calc(100vh-320px)]">
                {visibleDepartures.length === 0 ? (
                  <div className="px-4 py-8 text-center text-xs leading-relaxed text-muted">
                    No scheduled departures are available for this month.
                  </div>
                ) : (
                  visibleDepartures.map((departure) => {
                    const { label, tone } = statusLabel(departure);
                    const soldOut = label === "Sold out";
                    const departureDate = safeDate(departure.startDate);
                    const startMonth = departureDate
                      ? DEPARTURE_MONTH.format(departureDate)
                      : "—";
                    const startDay = departureDate
                      ? DEPARTURE_DAY.format(departureDate)
                      : "—";

                    return (
                      <article
                        key={departure.id}
                        className={cn(
                          "group grid grid-cols-[48px_minmax(0,1fr)_auto] items-center gap-2.5 bg-white px-1.5 py-3 transition-colors sm:gap-3 sm:px-2",
                          soldOut ? "opacity-50" : "hover:bg-paper/45",
                        )}
                      >
                        <div className="date relative h-11 w-11 shrink-0 flex flex-col pb-[6px] justify-end font-semibold items-center text-primary text-[9px] leading-[1.1] text-center">
                          <b className="text-[14px] font-extrabold pb-[5px] leading-none">
                            {startDay}
                          </b>
                          <span className="font-bold uppercase">
                            {startMonth}
                          </span>
                          <i className="icon h-11 w-11 absolute top-0 left-0 pointer-events-none">
                            <svg className="h-full w-full block">
                              <use
                                xlinkHref="/icons.svg#date"
                                fill="currentColor"
                              />
                            </svg>
                          </i>
                        </div>

                        <div className="min-w-0">
                          <p className="text-xs font-bold leading-snug text-headings">
                            {formatRange(
                              departure.startDate,
                              departure.endDate,
                            )}
                          </p>
                          <div className="mt-1.5 flex flex-wrap items-center gap-2">
                            <Badge
                              variant={tone}
                              className="shrink-0 px-1.5 py-0.5 font-bold tracking-wider text-[9px]"
                            >
                              {label}
                            </Badge>
                            {!soldOut && departure.spotsAvailable != null ? (
                              <span className="inline-flex items-center gap-1 text-[9px] font-semibold text-muted-soft">
                                <Users2 size={10} aria-hidden />
                                {departure.spotsAvailable} spots left
                              </span>
                            ) : null}
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-1.5">
                          {tierRowPrice > 0 ? (
                            <span className="text-xs font-extrabold tabular-nums text-primary">
                              {formatPrice(
                                tierRowPrice,
                                departure.currency || currency,
                              )}
                              <span className="ml-0.5 text-[8px] font-medium text-muted-soft">
                                pp
                              </span>
                            </span>
                          ) : null}
                          <BookingWizard
                            {...wizardProps}
                            currency={departure.currency || currency}
                            packageId={packageId ?? departure.packageId}
                            departureId={departure.id}
                            startDate={departureDate}
                            travelers={1}
                            trigger={
                              <button
                                type="button"
                                disabled={soldOut || !departureDate}
                                aria-label={`${soldOut ? "Sold out" : "Book now"}: ${formatRange(departure.startDate, departure.endDate)}`}
                                className="inline-flex items-center justify-center gap-1 rounded-full bg-headings text-white px-3 py-1.5 text-[10px] font-extrabold tracking-wide transition-all hover:bg-primary hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-2 active:translate-y-px disabled:cursor-not-allowed disabled:bg-line disabled:text-muted-soft motion-reduce:transition-none"
                              >
                                {soldOut ? "Sold Out" : "Book Now"}
                              </button>
                            }
                          />
                        </div>
                      </article>
                    );
                  })
                )}
              </div>
            </>
          ) : (
            <div>
              <label className="mb-2 block text-[10px] font-bold uppercase tracking-wide text-muted">
                Departure date
              </label>
              <div className="overflow-hidden rounded border border-line bg-paper/70">
                <button
                  type="button"
                  onClick={() => setShowCalendar((visible) => !visible)}
                  aria-expanded={showCalendar}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-sm font-semibold text-muted transition-colors hover:bg-white"
                >
                  <span>
                    {startDate
                      ? startDate.toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })
                      : "Select date"}
                  </span>
                  <CalendarDays size={17} aria-hidden />
                </button>
                {showCalendar ? (
                  <div className="border-t border-line bg-white p-2">
                    <DatePicker
                      selected={startDate ?? undefined}
                      onSelect={(date) => {
                        setStartDate(date ?? null);
                        if (date) setShowCalendar(false);
                      }}
                      disabled={(date) => date < new Date()}
                      mode="single"
                    />
                  </div>
                ) : null}
              </div>
              <BookingWizard
                {...wizardProps}
                currency={currency}
                startDate={startDate}
                travelers={1}
                trigger={
                  <button
                    type="button"
                    disabled={!startDate}
                    className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded bg-primary px-5 py-3 text-xs font-extrabold uppercase tracking-wide text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:bg-line disabled:text-muted-soft"
                  >
                    {startDate ? "Book now" : "Select date"}
                    {startDate ? <ArrowRight size={13} aria-hidden /> : null}
                  </button>
                }
              />
            </div>
          )}

          {/* Private Date Inquiry Section */}
          <div className="mt-3">
            <div className="relative flex justify-center items-center gap-3 mt-3 overflow-hidden border-t border-line pt-4">
              <p className="text-[13px] font-bold text-headings leading-tight mb-1">
                Request a Private Date?
              </p>
              {/* <p className="text-[10px] text-muted mb-2.5 leading-normal">
                We organize tailored dates & custom itineraries for private
                groups.
              </p> */}
              <InquiryDialog
                title={`Private Date Inquiry: ${packageTitle}`}
                slug={packageSlug}
                trigger={
                  <button
                    type="button"
                    className="inline-flex whitespace-nowrap text-primary items-center justify-center gap-1.5 rounded-2xl border border-primary bg-white px-4 py-1.5 text-[11px] font-extrabold tracking-wider shadow-xs transition-all hover:bg-primary hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                  >
                    Make an Enquiry
                  </button>
                }
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 divide-x divide-line overflow-hidden rounded-sm border border-line bg-white">
        <div className="flex min-h-12 items-center justify-center gap-2 px-2 text-center text-[9px] font-bold uppercase tracking-wide text-muted">
          <LockKeyhole size={14} className="text-muted" aria-hidden />
          Secure payment
        </div>
        <div className="flex min-h-12 items-center justify-center gap-2 px-2 text-center text-[9px] font-bold uppercase tracking-wide text-muted">
          <svg
            width="18"
            height="18"
            className="inline-block text-whatsapp max-h-full max-w-full shrink-0"
            fill="currentColor"
            preserveAspectRatio="xMidYMid meet"
            aria-hidden="true"
            focusable="false"
          >
            <use
              href="/icons.svg#whatsapp"
              xlinkHref="/icons.svg#whatsapp"
            ></use>
          </svg>
          24/7 support
        </div>
      </div>
    </>
  );
}
